import { useRpcReadAccess } from 'src/app/AppAuth/useReadAccess'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useParams } from 'react-router-dom'
import styled, { css } from 'styled-components/macro'
import {
  ChatContextSourceEvidenceFor,
  ChatContextSourceType,
} from '@trustero/trustero-api-web/lib/chatcontext/chatcontext_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import {
  getChatContextFromGRPC,
  getUpdateChatContextRequest,
} from 'src/components/Reusable/ChatContext/chatContext.utils'
import { P } from 'src/components/Reusable/Text/Text.styles'
import { FlexAlign, FlexColumn, FlexRow } from 'src/components/Reusable/Flex'
import { DocumentSourceProvider } from 'src/context/DocumentSource/DocumentSourceContext'
import palette from 'src/designSystem/variables/palette'
import { ReactComponent as EroEastIcon } from 'src/components/Icons/Brand/ero_dark_east.svg'
import { faPen, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  AddSVG,
  ChatsSVG,
  CloseSVG,
  PrivateSVG,
  PublicSVG,
} from 'src/components/Icons/Basic'
import { TooltipIconButton } from 'src/components/Reusable/Buttons/TooltipIconButton'
import { GridRowMenu } from 'src/components/Reusable/Grid/GridRowMenu'
import { TrusteroIntelligenceAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { ArtifactPanelProvider } from 'src/pages/TrusteroIntelligence/chatpanel/chatPanel.context'
import { ArtifactModalProvider } from 'src/pages/TrusteroIntelligence/artifacts/ArtifactModalContext'
import { ChatContextServicePromiseClient } from '@trustero/trustero-api-web/lib/chatcontext/chatcontext_grpc_web_pb'
import {
  ChatStateProvider,
  useChatState,
} from 'src/pages/TrusteroIntelligence/trusteroIntelligence.context'
import {
  useGetChat,
  useUpdateChat,
} from 'src/pages/TrusteroIntelligence/trusteroIntelligence.hooks'
import { useAuthorizedGrpcClient } from 'src/adapter'
import { useUser } from 'src/components/async/Users'
import { Chat } from '@trustero/trustero-api-web/lib/chat/chat_pb'
import { CopilotBot } from '../CopilotBot'
import { readLastVisitedChat, useAppCopilotContext } from './AppCopilot.context'
import { CopilotLibraryView } from './CopilotLibraryView'
import { CopilotHistoryPanel } from './CopilotHistoryPanel'

const LazyChatConversationPanel = React.lazy(async () => {
  const module = await import(
    'src/pages/TrusteroIntelligence/conversation/ChatConversationPanel'
  )
  return { default: module.ChatConversationPanel }
})

const AppCopilotContainer = styled.div<{
  $widthPercent: number
  $open: boolean
  $isResizing: boolean
}>`
  position: relative;
  height: 100%;
  flex: 0 0
    ${({ $open, $widthPercent }) => ($open ? `${$widthPercent}%` : '0%')};
  width: ${({ $open, $widthPercent }) => ($open ? `${$widthPercent}%` : '0%')};
  min-width: ${({ $open }) => ($open ? '700px' : '0')};
  max-width: ${({ $open }) => ($open ? 'calc(66% - 12px)' : '0')};
  margin-right: ${({ $open }) => ($open ? '12px' : '0')};
  background: ${palette.neutral.white};
  border-top-left-radius: 20px;
  box-shadow: -8px 0px 20px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  overflow: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: ${({ $isResizing }) =>
    $isResizing
      ? 'none'
      : 'width 0.25s ease, flex-basis 0.25s ease, min-width 0.25s ease, margin-right 0.25s ease'};
  cursor: ${({ $isResizing }) => ($isResizing ? 'ew-resize' : 'default')};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 38px;
    bottom: 0;
    width: 2px;
    background: linear-gradient(180deg, #bb83ff 0%, #4381fd 100%);
    pointer-events: none;
  }
`

const IconStyle = css`
  cursor: pointer;
  path {
    fill: ${palette.neutral.white};
  },
`

const AppCopilotHeader = styled.div`
  min-height: 38px;
  max-height: 38px;
  background: linear-gradient(90deg, #bb83ff 0%, #4381fd 100%);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top-left-radius: 20px;
  padding: 0 12px;
`

const ActionIconsRow = styled(FlexRow)`
  gap: 12px;

  .dropdown-menu {
    min-width: fit-content !important;
    padding: 0 !important;
  }

  .dropdown-toggle {
    width: 16px;
    svg {
      height: 16px;
      ${IconStyle}
    }
  }

  svg:hover {
    path {
      fill: ${palette.neutral[50]};
    }
  }

  button.dropdown-toggle:hover {
    background-color: transparent !important;
  }
`

const CloseButton = styled(CloseSVG)`
  width: 12px;
  height: 12px;
  ${IconStyle}
`

const HeaderEroIcon = styled(EroEastIcon)`
  width: 16px;
  height: 16px;
`

const HeaderAddIcon = styled(AddSVG)`
  width: 16px;
  height: 16px;
  ${IconStyle}
`

const HeaderChatsIcon = styled(ChatsSVG)`
  width: 16px;
  height: 16px;
  ${IconStyle}
`

const CopilotBody = styled(FlexColumn)`
  height: calc(100% - 38px);
  width: 100%;
  align-items: stretch;
  overflow: hidden;
  position: relative;
`

const CurrentContextContainer = styled.div`
  display: inline-flex;
  align-items: flex-start;
  align-self: flex-start;
  width: fit-content;
  max-width: 100%;
  margin: 0 4px 0;
`

const ContextPill = styled.div`
  display: inline-flex;
  padding: 8px 16px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 12px;
  border: 1px solid ${palette.neutral[100]};
  max-width: 100%;
`

const ContextPillText = styled.span`
  color: ${palette.neutral[600]};
  font-family: Poppins;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ContextPillCloseButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  color: ${palette.neutral[600]};
`

const ContextPillCloseIcon = styled(CloseSVG)`
  width: 14px;
  height: 14px;
  path {
    fill: ${palette.neutral[600]};
  }
`

const CopilotTitleWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`

const CopilotTitleText = styled.span`
  color: ${palette.neutral.white};
  font-family: Poppins;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.024px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
`

const CopilotTitleInput = styled.input`
  color: ${palette.neutral.white};
  background: transparent;
  border: none;
  border-bottom: 1px solid ${palette.neutral.white};
  font-family: Poppins;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.024px;
  outline: none;
  min-width: 500px;
  padding: 0;
  &::selection {
    background: rgba(255, 255, 255, 0.3);
  }
`

const CopilotTitleIconBtn = styled.button`
  background: none;
  border: none;
  padding: 2px 4px;
  cursor: pointer;
  color: ${palette.neutral.white};
  display: flex;
  align-items: center;
  opacity: 0.8;
  &:hover {
    opacity: 1;
  }
`

type CopilotChatTitleProps = {
  chat: InstanceType<typeof Chat> | undefined
  mutateGetChat: (() => Promise<unknown>) | undefined
}

const CopilotChatTitle = ({
  chat,
  mutateGetChat,
}: CopilotChatTitleProps): JSX.Element => {
  const { chatTitle } = useChatState(chat?.getId() ?? '')
  const { data: user } = useUser()
  const canCreateChats = useCanPerformAction(CAPABILITY.CAN_CREATE_CHATS)
  const updateChat = useUpdateChat()
  const [isEditing, setIsEditing] = useState(false)
  const [tempName, setTempName] = useState('')
  const [isHovered, setIsHovered] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const chatName = chatTitle || chat?.getName() || ''
  const hasMessages =
    (chat?.getConversation()?.getMessagesList()?.length ?? 0) > 0
  const isReadOnly =
    !canCreateChats ||
    chat?.getOwner()?.getId() !== user?.getId() ||
    !hasMessages

  const startEdit = useCallback(() => {
    if (isReadOnly) return
    setTempName(chatName)
    setIsEditing(true)
    setTimeout(() => inputRef.current?.select(), 0)
  }, [isReadOnly, chatName])

  const cancelEdit = useCallback(() => {
    setIsEditing(false)
    setIsHovered(false)
  }, [])

  const saveEdit = useCallback(async () => {
    const trimmed = tempName.trim()
    if (trimmed && trimmed !== chatName && chat) {
      const updatedChat = new Chat()
        .setId(chat.getId())
        .setName(trimmed)
        .setIsPrivate(chat.getIsPrivate())
        .setModel(chat.getModel())
      await updateChat(updatedChat)
      await mutateGetChat?.()
    }
    setIsEditing(false)
    setIsHovered(false)
  }, [tempName, chatName, chat, updateChat, mutateGetChat])

  if (isEditing) {
    return (
      <CopilotTitleWrapper
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void saveEdit()
          if (e.key === 'Escape') cancelEdit()
        }}
      >
        <CopilotTitleInput
          ref={inputRef}
          value={tempName}
          onChange={(e) => setTempName(e.target.value)}
          autoFocus
        />
        <CopilotTitleIconBtn onClick={() => void saveEdit()} title="Save">
          <FontAwesomeIcon icon={faCheck} style={{ fontSize: '11px' }} />
        </CopilotTitleIconBtn>
        <CopilotTitleIconBtn onClick={cancelEdit} title="Cancel">
          <FontAwesomeIcon icon={faTimes} style={{ fontSize: '11px' }} />
        </CopilotTitleIconBtn>
      </CopilotTitleWrapper>
    )
  }

  return (
    <CopilotTitleWrapper
      onMouseEnter={() => !isReadOnly && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CopilotTitleText onClick={startEdit} title={chatName}>
        {chatName}
      </CopilotTitleText>
      {isHovered && (
        <CopilotTitleIconBtn
          onClick={startEdit}
          title="Edit title"
          style={{ marginLeft: 4, flexShrink: 0 }}
        >
          <FontAwesomeIcon icon={faPen} style={{ fontSize: '10px' }} />
        </CopilotTitleIconBtn>
      )}
    </CopilotTitleWrapper>
  )
}

const VisibilityMenuItem = ({
  isPrivate,
  onClick,
}: {
  isPrivate: boolean
  onClick: () => void
}): JSX.Element => (
  <span
    onClick={onClick}
    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
  >
    {isPrivate ? (
      <PrivateSVG width="14px" height="14px" />
    ) : (
      <PublicSVG width="14px" height="14px" />
    )}
    {isPrivate ? 'Private — make public' : 'Public — make private'}
  </span>
)

const DragHandle = styled.button`
  position: absolute;
  left: -4px;
  top: 50%;
  transform: translateY(-50%);
  width: 10px;
  height: 21px;
  border: none;
  border-radius: 20px;
  background: linear-gradient(180deg, #b583ff 0%, #5381fd 100%);
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
  cursor: ew-resize;
  padding: 0;
  z-index: 2;

  &:hover {
    cursor: ew-resize;
  }
`

export const AppCopilot = (): JSX.Element =>
  useRpcReadAccess('/chat.ChatService/ListAppCopilotChats') ? (
    <AppCopilotContent />
  ) : (
    <></>
  )

const AppCopilotContent = (): JSX.Element => {
  const {
    title,
    show,
    setShow,
    showBotTrigger,
    view,
    setView,
    historyOpen,
    setHistoryOpen,
    chatId,
    setChatId,
    referringContext,
    referringContextMetadata,
    currentContextLabel,
    copilotDestination,
    widthPercent,
    setWidthPercent,
    registerSendMessage,
    setIsChatBusy,
    isCurrentContextDismissed,
    setIsCurrentContextDismissed,
    createCopilotChat,
    writeLastVisitedChat,
  } = useAppCopilotContext()
  const params = useParams<{ pageContext: string }>()
  const chatContextClient = useAuthorizedGrpcClient(
    ChatContextServicePromiseClient,
  )
  const {
    data: chatResponse,
    isLoading,
    error: chatError,
    mutate: mutateGetChat,
  } = useGetChat(chatId ?? '')
  const chat = useMemo(() => chatResponse?.getChat(), [chatResponse])

  const { data: user } = useUser()
  const updateChat = useUpdateChat()
  const [isPrivate, setIsPrivate] = useState<boolean>(true)

  useEffect(() => {
    if (chat) {
      setIsPrivate(chat.getIsPrivate())
    }
  }, [chat])

  const isOwner = useMemo(
    () => !!chat && chat.getOwner()?.getId() === user?.getId(),
    [chat, user],
  )

  const togglePrivate = useCallback(async () => {
    if (!chat || !isOwner) return
    const newValue = !isPrivate
    chat.setIsPrivate(newValue)
    setIsPrivate(newValue)
    try {
      await updateChat(chat)
    } catch {
      chat.setIsPrivate(!newValue)
      setIsPrivate(!newValue)
    }
  }, [chat, isOwner, isPrivate, updateChat])

  const dragStartXRef = useRef<number>()
  const dragStartWidthRef = useRef<number>()
  const rafRef = useRef<number>()
  const pendingWidthRef = useRef<number>()
  const lastSyncedContextKeyRef = useRef<string>()
  const [isResizing, setIsResizing] = useState(false)
  // Track whether we've restored chatId from localStorage for the current session
  const hasRestoredFromStorageRef = useRef(false)

  const referringContextMetadataKey = useMemo(
    () =>
      `${referringContextMetadata?.filterQuery || ''}::${(
        referringContextMetadata?.resultIds || []
      ).join(',')}::${referringContextMetadata?.resultsCount || 0}`,
    [
      referringContextMetadata?.filterQuery,
      referringContextMetadata?.resultIds,
      referringContextMetadata?.resultsCount,
    ],
  )

  // Restore last visited chat from localStorage when panel opens
  useEffect(() => {
    if (!show || hasRestoredFromStorageRef.current) {
      return
    }
    if (chatId) {
      // chatId was set externally (e.g. from URL), go straight to conversation
      hasRestoredFromStorageRef.current = true
      setView('conversation')
      return
    }
    hasRestoredFromStorageRef.current = true
    const storedChatId = readLastVisitedChat(
      referringContext.objectType,
      referringContext.objectId,
    )
    if (storedChatId) {
      setChatId(storedChatId)
      // We'll let useGetChat load it; if it errors, the error effect below
      // will fall back to library view.
    } else {
      setView('library')
    }
  }, [
    show,
    chatId,
    referringContext.objectType,
    referringContext.objectId,
    setChatId,
    setView,
  ])

  // When chatId is set but chat hasn't loaded yet, determine view once loaded
  useEffect(() => {
    if (!chatId || isLoading) {
      return
    }
    if (chat) {
      setView('conversation')
    }
  }, [chatId, chat, isLoading, setView])

  // If a restored chatId fails to load, fall back to library
  useEffect(() => {
    if (!chatId || !chatError) {
      return
    }
    setChatId(undefined)
    setView('library')
    writeLastVisitedChat('')
  }, [chatId, chatError, setChatId, setView, writeLastVisitedChat])

  // Reset per-object state when referring context changes
  useEffect(() => {
    hasRestoredFromStorageRef.current = false
    lastSyncedContextKeyRef.current = undefined
  }, [referringContext.objectType, referringContext.objectId])

  const updateChatContextForCopilot = useCallback(
    async (clearCurrentContext: boolean) => {
      if (!chat) {
        return
      }
      const chatContextGRPC = chat.getChatContext()
      const chatContextId = chatContextGRPC?.getId()
      if (!chatContextGRPC || !chatContextId) {
        return
      }

      const chatContext = getChatContextFromGRPC(chatContextGRPC)

      if (referringContext.objectType === MODEL_TYPE.CONTROL) {
        const controlId = referringContext.objectId || ''
        chatContext[ChatContextSourceType.CONTROL].includeAll =
          clearCurrentContext || !controlId
        chatContext[ChatContextSourceType.CONTROL].included =
          clearCurrentContext ? new Set() : new Set([controlId])
      }

      // A vendor show page scopes the chat to that one vendor, the same way
      // a control show page does. Without this the copilot opens with the
      // whole account in context and the "Context: <vendor>" chip in the UI
      // means nothing to the agent (AP-20383).
      if (referringContext.objectType === MODEL_TYPE.VENDOR) {
        const vendorId = referringContext.objectId || ''
        chatContext[ChatContextSourceType.VENDORS].includeAll =
          clearCurrentContext || !vendorId
        chatContext[ChatContextSourceType.VENDORS].included =
          clearCurrentContext ? new Set() : new Set([vendorId])
      }

      if (referringContext.objectType === MODEL_TYPE.EVIDENCE) {
        const resultIds = referringContextMetadata?.resultIds || []
        const includeAll = clearCurrentContext || resultIds.length === 0
        chatContext[ChatContextSourceType.EVIDENCE].includeAll = includeAll
        chatContext[ChatContextSourceType.EVIDENCE].included = includeAll
          ? new Set()
          : new Set(resultIds)
        chatContext[ChatContextSourceType.EVIDENCE].type = includeAll
          ? {}
          : resultIds.reduce<Record<string, number>>((acc, id) => {
              acc[id] = ChatContextSourceEvidenceFor.FOR_SELF
              return acc
            }, {})
      }

      const request = getUpdateChatContextRequest(chatContextId, chatContext)
      await chatContextClient.updateChatContext(request)
      await mutateGetChat?.()
    },
    [
      chat,
      chatContextClient,
      mutateGetChat,
      referringContext.objectId,
      referringContext.objectType,
      referringContextMetadata?.resultIds,
    ],
  )

  useEffect(() => {
    if (!show || !chat || isCurrentContextDismissed) {
      return
    }

    const syncKey = [
      chat.getId(),
      referringContext.objectType,
      referringContext.objectId || '',
      referringContextMetadataKey,
    ].join('::')

    if (lastSyncedContextKeyRef.current === syncKey) {
      return
    }

    lastSyncedContextKeyRef.current = syncKey
    void updateChatContextForCopilot(false)
  }, [
    show,
    chat,
    isCurrentContextDismissed,
    referringContext.objectType,
    referringContext.objectId,
    referringContextMetadataKey,
    updateChatContextForCopilot,
  ])

  const onClearCurrentContext = useCallback(() => {
    setIsCurrentContextDismissed(true)
    void updateChatContextForCopilot(true)
  }, [setIsCurrentContextDismissed, updateChatContextForCopilot])

  const onOpenInTi = useCallback(() => {
    if (!chatId || !params.pageContext) {
      return
    }
    window.open(
      `/${params.pageContext}/${TrusteroIntelligenceAbsoluteRoutes.SHOW}/${chatId}`,
      '_blank',
      'noopener,noreferrer',
    )
  }, [chatId, params.pageContext])

  const menuItems = useMemo(
    () => [
      {
        key: 'open_in_ti',
        item: <span onClick={onOpenInTi}>Open in TI</span>,
      },
      ...(isOwner
        ? [
            {
              key: 'visibility',
              item: (
                <VisibilityMenuItem
                  isPrivate={isPrivate}
                  onClick={togglePrivate}
                />
              ),
            },
          ]
        : []),
    ],
    [onOpenInTi, isPrivate, isOwner, togglePrivate],
  )

  const onNewChat = useCallback(() => {
    setChatId(undefined)
    setView('library')
    setHistoryOpen(false)
    writeLastVisitedChat('')
  }, [setChatId, setView, setHistoryOpen, writeLastVisitedChat])

  const onMouseMove = useCallback(
    (event: MouseEvent) => {
      if (
        dragStartXRef.current === undefined ||
        dragStartWidthRef.current === undefined
      ) {
        return
      }
      const deltaPx = dragStartXRef.current - event.clientX
      const deltaPercent = (deltaPx / window.innerWidth) * 100
      pendingWidthRef.current = dragStartWidthRef.current + deltaPercent
      if (rafRef.current !== undefined) {
        return
      }
      rafRef.current = window.requestAnimationFrame(() => {
        if (pendingWidthRef.current !== undefined) {
          setWidthPercent(pendingWidthRef.current)
        }
        rafRef.current = undefined
      })
    },
    [setWidthPercent],
  )

  const onMouseUp = useCallback(() => {
    dragStartXRef.current = undefined
    dragStartWidthRef.current = undefined
    setIsResizing(false)
    document.body.style.cursor = ''
    document.documentElement.style.cursor = ''
    if (rafRef.current !== undefined) {
      window.cancelAnimationFrame(rafRef.current)
      rafRef.current = undefined
    }
    if (pendingWidthRef.current !== undefined) {
      setWidthPercent(pendingWidthRef.current)
      pendingWidthRef.current = undefined
    }
  }, [setWidthPercent])

  const onResizeStart = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()
      dragStartXRef.current = event.clientX
      dragStartWidthRef.current = widthPercent
      setIsResizing(true)
      document.body.style.cursor = 'ew-resize'
      document.documentElement.style.cursor = 'ew-resize'
    },
    [widthPercent],
  )

  useEffect(() => {
    if (!show && isResizing) {
      onMouseUp()
    }
  }, [show, isResizing, onMouseUp])

  useEffect(() => {
    if (!isResizing) {
      return
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [isResizing, onMouseMove, onMouseUp])

  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
      if (rafRef.current !== undefined) {
        window.cancelAnimationFrame(rafRef.current)
        rafRef.current = undefined
      }
    }
  }, [onMouseMove, onMouseUp])

  const shouldShowCurrentContext =
    show && !!currentContextLabel && !isCurrentContextDismissed
  const promptTopContent = shouldShowCurrentContext ? (
    <CurrentContextContainer>
      <ContextPill>
        <ContextPillText>{`Context: ${currentContextLabel}`}</ContextPillText>
        <ContextPillCloseButton
          aria-label="Clear current context"
          onClick={onClearCurrentContext}
        >
          <ContextPillCloseIcon />
        </ContextPillCloseButton>
      </ContextPill>
    </CurrentContextContainer>
  ) : null

  const promptLayoutKey = useMemo(
    () => `${show ? 'open' : 'closed'}-${chatId || view}`,
    [show, chatId, view],
  )

  const onFirstMessage = useCallback(async () => {
    if (!mutateGetChat) {
      return
    }
    await mutateGetChat()
  }, [mutateGetChat])

  // Header title: static in library view, editable in conversation view
  const headerTitle =
    view === 'conversation' ? (
      <CopilotChatTitle chat={chat} mutateGetChat={mutateGetChat} />
    ) : (
      <P $fontSize={16} $fontweight={400} $color={palette.neutral.white}>
        {title}
      </P>
    )

  return (
    <>
      {showBotTrigger && !show && <CopilotBot onClick={() => setShow(true)} />}
      <AppCopilotContainer
        $widthPercent={widthPercent}
        $open={show}
        $isResizing={isResizing}
      >
        {show && (
          <DragHandle
            aria-label="Resize copilot panel"
            onMouseDown={onResizeStart}
          />
        )}
        <ChatStateProvider>
          <AppCopilotHeader>
            <FlexRow
              align={FlexAlign.CENTER}
              gap={4}
              style={{ flex: 1, minWidth: 0, justifyContent: 'flex-start' }}
            >
              <HeaderEroIcon />
              {headerTitle}
            </FlexRow>
            <ActionIconsRow align={FlexAlign.CENTER}>
              <TooltipIconButton
                icon={<HeaderAddIcon />}
                tooltipText="New Chat"
                onClick={onNewChat}
                preserveIconStyles
              />
              <TooltipIconButton
                icon={<HeaderChatsIcon />}
                tooltipText="Chats"
                onClick={() => setHistoryOpen(!historyOpen)}
                preserveIconStyles
              />
              {view === 'conversation' && chatId && (
                <GridRowMenu
                  id={`app-copilot-header-menu-${chatId}`}
                  items={menuItems}
                />
              )}
              <CloseButton onClick={() => setShow(false)} />
            </ActionIconsRow>
          </AppCopilotHeader>
          <CopilotBody>
            {view === 'library' && (
              <ArtifactPanelProvider>
                <CopilotLibraryView
                  promptTopContent={promptTopContent}
                  createCopilotChat={createCopilotChat}
                  copilotDestination={copilotDestination}
                />
              </ArtifactPanelProvider>
            )}
            {view === 'conversation' && (
              <Suspense
                fallback={
                  <P
                    $fontSize={13}
                    $color={palette.neutral[400]}
                    style={{ padding: 12 }}
                  >
                    Loading conversation...
                  </P>
                }
              >
                <DocumentSourceProvider>
                  <ArtifactPanelProvider>
                    <ArtifactModalProvider>
                      <LazyChatConversationPanel
                        chat={chat}
                        isPrivate={isPrivate}
                        setIsPrivate={setIsPrivate}
                        onFirstMessage={onFirstMessage}
                        isLoading={!chatId || isLoading}
                        isCopilotMode={true}
                        onRegisterSendMessage={registerSendMessage}
                        onBusyChange={setIsChatBusy}
                        promptTopContent={promptTopContent}
                        promptLayoutKey={promptLayoutKey}
                      />
                    </ArtifactModalProvider>
                  </ArtifactPanelProvider>
                </DocumentSourceProvider>
              </Suspense>
            )}
            <CopilotHistoryPanel />
          </CopilotBody>
        </ChatStateProvider>
      </AppCopilotContainer>
    </>
  )
}
